import React from 'react';
import ReactDOM from 'react-dom/client';

// third-party
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import { gql } from '@apollo/client';

// project imports
import * as serviceWorker from 'serviceWorker';
import App from 'App';
import { store } from 'store';
import { apolloClient } from './services/api';

// style + assets
import 'assets/scss/style.scss';

// ==============================|| CRITICAL DATA PRELOADING ||============================== //

// Preload critical queries on app start
const preloadCriticalData = async () => {
    try {
        // Only preload if user is authenticated
        const token = sessionStorage.getItem('token');
        if (token) {
            // Preload user data
            await apolloClient.query({
                query: gql`
                    query PreloadUserData {
                        me {
                            id
                            eventId
                            permissions
                            role
                        }
                    }
                `,
                fetchPolicy: 'network-only',
            });
        }
    } catch (error) {
        console.log('Preload failed:', error);
    }
};

// Optimize bundle splitting and lazy loading
const root = ReactDOM.createRoot(document.getElementById('root'));

// Enhanced error boundary
class AppErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('App Error:', error, errorInfo);

        // In production, you might want to send this to an error reporting service
        if (process.env.NODE_ENV === 'production') {
            // Send to error reporting service
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div
                    style={{
                        padding: '2rem',
                        textAlign: 'center',
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <h2>Something went wrong</h2>
                    <p>We're sorry for the inconvenience. Please refresh the page or try again later.</p>
                    <button
                        onClick={() => {
                            this.setState({ hasError: false, error: null });
                            window.location.reload();
                        }}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginTop: '1rem',
                        }}
                    >
                        Refresh Page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

// Render app with optimizations
root.render(
    <React.StrictMode>
        <AppErrorBoundary>
            <Provider store={store}>
                <BrowserRouter basename='/'>
                    <ApolloProvider client={apolloClient}>
                        <App />
                    </ApolloProvider>
                </BrowserRouter>
            </Provider>
        </AppErrorBoundary>
    </React.StrictMode>
);

// Initialize critical data preloading
preloadCriticalData();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
